// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import OrderButtonDoorDash from '../components/OrderButtonDoorDash';
import OrderButtonCaviar from '../components/OrderButtonCaviar';
import PickupButton from '../components/PickupButton';
import MenuButton from '../components/MenuButton';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  @media (max-width: 768px) { flex-direction: column; }
`;

const Left = styled.div`
  flex: 1;
  position: relative;
  background-color: #000;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 50px;
  overflow: hidden;
  @media (max-width: 768px) { padding-right: 0; padding-top: 20px; justify-content: center; }
`;

const Slide = styled.div`
  position: absolute; inset: 0;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.src});
  opacity: ${props => (props.visible ? 1 : 0)};
  transition: opacity 1s ease-in-out;
`;

const Logo = styled.img`
  width: 300px;
  position: relative;
  max-width: 100%;
  height: auto;
  @media (max-width: 768px) { width: 150px; }
`;

const Right = styled.div`
  flex: 1;
  background-image: url('/right-bg.jpg');
  background-size: cover;
  background-position: center;
  display: flex; flex-direction: column;
  justify-content: center; align-items: flex-start;
  padding-left: 50px;
  @media (max-width: 768px) { padding-left: 0; padding-top: 20px; align-items: center; }
`;

const Title = styled.h1`
  font-size: 2.5rem; margin-bottom: 20px; color: #e1d6c0;
  background-color: rgba(0,0,0,.6); padding: 10px 20px; border-radius: 8px; display: inline-block;
`;

const Info = styled.div`
  font-size: 1rem; line-height: 1.4; color: #fff; margin-bottom: 20px;
`;

const Buttons = styled.div`
  display: flex; flex-direction: column; align-items: flex-start;
  gap: 20px; margin-top: 20px;
`;

const OrderOnlineBtn = styled.button`
  display: inline-block;
  padding: 15px 30px;
  font-size: 1.2rem;
  border-radius: 30px;
  background: #e1d6c0;
  color: #0d0d0d;
  font-weight: 700;
  transition: opacity 0.2s;
  text-align: center;
  &:hover { opacity: 0.8; }
  cursor: pointer;
  text-decoration: underline;
`;

const Footer = styled.footer`
  margin-top: 40px; text-align: center; display: flex; flex-direction: column; align-items: center; width: 100%;
`;

const FollowText = styled.div`
  color: #e1d6c0; font-size: 1.5rem; margin-bottom: 12px;
`;

const SocialIcons = styled.div` display: inline-flex; gap: 16px; `;
const IconLink = styled.a`
  color: #e1d6c0; font-size: 2.5rem; transition: color .2s;
  &:hover { color: #fff; }
`;

/* ---------- Modal ---------- */
const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 50;
`;

const Modal = styled.div`
  width: 92%; max-width: 520px;
  background: rgba(13,13,13,0.92);
  border: 1px solid #333; border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
`;

const ModalTitle = styled.h3`
  margin: 0 0 14px; color: #e1d6c0; font-size: 1.25rem;
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const CloseBar = styled.div`
  display: flex; justify-content: flex-end; margin-top: 14px;
`;

const CloseBtn = styled.button`
  background: transparent; color: #e1d6c0;
  border: 1px solid #e1d6c0; border-radius: 999px;
  padding: 8px 14px; cursor: pointer;
`;

/* Optional: a simple pickup button to match modal look */
const PickupBtn = styled.button`
  background: #e1d6c0; color: #0d0d0d;
  border: none; border-radius: 10px;
  padding: 12px 16px; font-weight: 700; cursor: pointer; width: 100%;
`;

const Home = () => {
  const navigate = useNavigate();
  const slideImages = ['/left-bg1.jpg', '/left-bg2.jpg', '/left-bg3.jpg', '/left-bg4.jpg', '/left-bg5.jpg'];
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrent(c => (c + 1) % slideImages.length);
        setVisible(true);
      }, 2500);
    }, 7000);
    return () => clearInterval(cycle);
  }, [slideImages.length]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);
  const goPickup = () => { setOpen(false); navigate('/menu'); };

  return (
    <Container>
      <Left>
        <Slide src={slideImages[current]} visible={visible} />
        <Logo src="/logo.png" alt="ZOKU" />
      </Left>

      <Right>
        <Title>Welcome to ZOKU | Contemporary Japanese</Title>
        <Info>
          <div><strong>Hours:</strong> Mon-Thu 5pm–9pm | Fri-Sun 11:30am–2pm, 5pm–9pm</div>
          <div><strong>Location:</strong> 1846 W. 11th St., Tracy, CA 95376</div>
        </Info>

        <Buttons>
          <OrderOnlineBtn onClick={openModal}>Order Online</OrderOnlineBtn>
          {/* Keep a plain menu button on the page if you still want it visible */}
          <MenuButton />
        </Buttons>

        <Footer>
          <FollowText>Follow us!</FollowText>
          <SocialIcons>
            <IconLink
              href="https://www.facebook.com/people/Zoku-Zoku/pfbid0sfk9cLu79a2FEtHHAGr721yDKiC74V76B25pUpDJUrDc6QiUUC7eupPkiNoFgu5il/"
              target="_blank" rel="noopener" aria-label="Facebook">
              <FaFacebookF />
            </IconLink>
            <IconLink
              href="https://www.instagram.com/zoku.co/"
              target="_blank" rel="noopener" aria-label="Instagram">
              <FaInstagram />
            </IconLink>
          </SocialIcons>
        </Footer>
      </Right>

      {open && (
        <Backdrop onClick={closeModal} aria-hidden>
          <Modal
            role="dialog"
            aria-modal="true"
            aria-label="Choose an ordering method"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalTitle>Order Online</ModalTitle>
            <ModalRow>
              {/* Pickup → Menu */}
              <PickupButton />

              {/* Delivery buttons (reuse your existing components) */}
              <OrderButtonDoorDash />
              <OrderButtonCaviar />
            </ModalRow>

            <CloseBar>
              <CloseBtn onClick={closeModal}>Close</CloseBtn>
            </CloseBar>
          </Modal>
        </Backdrop>
      )}
    </Container>
  );
};

export default Home;
